import React, { useState, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import PaymentGate from '../components/PaymentGate';
import '../styles/design-system.css';

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactReason, setContactReason] = useState('');

  // Mock data for demonstration
  const mockConversations = [
    {
      id: 1,
      user: {
        id: 'user1',
        name: 'Sarah M.',
        photo: '/default-avatar.png',
        personalityType: 'Social Butterfly',
        personalityScore: 85,
        isPaidMember: true
      },
      lastMessage: {
        text: 'Hi! I saw you\'re interested in the London Pub Night. Would you like to join us?',
        timestamp: '2024-02-10T14:30:00Z',
        sender: 'user1'
      },
      unreadCount: 1,
      canReply: true,
      contactPermission: 'granted'
    },
    {
      id: 2,
      user: {
        id: 'user2',
        name: 'Mike R.',
        photo: '/default-avatar.png',
        personalityType: 'Adventure Seeker',
        personalityScore: 92,
        isPaidMember: true
      },
      lastMessage: {
        text: 'Thanks for the invite! I\'d love to join the hiking trip.',
        timestamp: '2024-02-09T16:45:00Z',
        sender: 'currentUser'
      },
      unreadCount: 0,
      canReply: true,
      contactPermission: 'granted'
    },
    {
      id: 3,
      user: {
        id: 'user3',
        name: 'Emma L.',
        photo: '/default-avatar.png',
        personalityType: 'Cultural Explorer',
        personalityScore: 78,
        isPaidMember: false
      },
      lastMessage: {
        text: 'I\'d love to join your museum visit! When is it?',
        timestamp: '2024-02-08T11:20:00Z',
        sender: 'user3'
      },
      unreadCount: 0,
      canReply: false,
      contactPermission: 'pending',
      needsUpgrade: true
    }
  ];

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        const subscriptionStatus = attributes['custom:subscriptionStatus'] || 'free';
        
        setUserProfile({
          id: currentUser.userId,
          name: attributes.name || 'User',
          email: attributes.email,
          personalityType: attributes['custom:personalityType'],
          personalityScore: attributes['custom:personalityScore']
        });
        
        setUserSubscriptionStatus(subscriptionStatus);
        setConversations(mockConversations);
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const handleSendMessage = (conversationId) => {
    if (!newMessage.trim()) return;

    // This would integrate with your backend to actually send the message
    console.log('Sending message:', newMessage, 'to conversation:', conversationId);
    
    // Update local state
    const updatedConversations = conversations.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          lastMessage: {
            text: newMessage,
            timestamp: new Date().toISOString(),
            sender: 'currentUser'
          },
          unreadCount: 0
        };
      }
      return conv;
    });
    
    setConversations(updatedConversations);
    setNewMessage('');
  };

  const handleRequestContact = (userId, reason) => {
    // This would send a contact request to the user
    console.log('Requesting contact with user:', userId, 'reason:', reason);
    setShowContactModal(false);
    setContactReason('');
  };

  const renderConversationList = () => {
    return (
      <div className="space-y-2">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            onClick={() => setSelectedConversation(conversation)}
            className={`p-4 rounded-lg cursor-pointer transition-colors ${
              selectedConversation?.id === conversation.id
                ? 'bg-primary text-white'
                : 'bg-white hover:bg-gray-50 border border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <img
                src={conversation.user.photo}
                alt={conversation.user.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium truncate">{conversation.user.name}</h4>
                  {conversation.unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                      {conversation.unreadCount}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 truncate">
                  {conversation.lastMessage.text}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">
                    {new Date(conversation.lastMessage.timestamp).toLocaleDateString()}
                  </span>
                  {conversation.user.isPaidMember && (
                    <span className="text-xs bg-success text-white px-2 py-1 rounded">
                      Paid Member
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderConversationDetail = () => {
    if (!selectedConversation) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <div className="text-4xl mb-4">💬</div>
            <p>Select a conversation to start messaging</p>
          </div>
        </div>
      );
    }

    const isPaidUser = userSubscriptionStatus === 'paid' || userSubscriptionStatus === 'premium';
    const canReply = selectedConversation.canReply || isPaidUser;

    return (
      <div className="flex flex-col h-full">
        {/* Conversation Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <img
              src={selectedConversation.user.photo}
              alt={selectedConversation.user.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1">
              <h3 className="font-semibold">{selectedConversation.user.name}</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedConversation.user.personalityType}
                </span>
                {isPaidUser && (
                  <span className="text-sm text-primary font-medium">
                    {selectedConversation.user.personalityScore}% Match
                  </span>
                )}
                {selectedConversation.user.isPaidMember && (
                  <span className="text-xs bg-success text-white px-2 py-1 rounded">
                    Paid Member
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
          <div className="space-y-4">
            {/* Sample messages */}
            <div className="flex justify-start">
              <div className="bg-white rounded-lg p-3 max-w-xs">
                <p className="text-sm">Hi! I saw you're interested in the London Pub Night. Would you like to join us?</p>
                <p className="text-xs text-gray-500 mt-1">2:30 PM</p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <div className="bg-primary text-white rounded-lg p-3 max-w-xs">
                <p className="text-sm">That sounds great! When is it?</p>
                <p className="text-xs opacity-75 mt-1">2:32 PM</p>
              </div>
            </div>
            
            <div className="flex justify-start">
              <div className="bg-white rounded-lg p-3 max-w-xs">
                <p className="text-sm">This Friday at 7 PM at The Red Lion. We have 5 people so far!</p>
                <p className="text-xs text-gray-500 mt-1">2:35 PM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 bg-white">
          {canReply ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="form-input flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(selectedConversation.id)}
              />
              <button
                onClick={() => handleSendMessage(selectedConversation.id)}
                disabled={!newMessage.trim()}
                className="btn btn-primary"
              >
                Send
              </button>
            </div>
          ) : (
            <PaymentGate
              feature="messages"
              userSubscriptionStatus={userSubscriptionStatus}
              customMessage="Upgrade to reply to this message and continue the conversation!"
            >
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  You need to upgrade to reply to this message
                </p>
                <button className="btn btn-primary">
                  Upgrade to Reply
                </button>
              </div>
            </PaymentGate>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
              <p className="text-gray-600">
                Connect with your travel companions and organize meetups
              </p>
            </div>
            
            <PaymentGate
              feature="messages"
              userSubscriptionStatus={userSubscriptionStatus}
            >
              <button
                onClick={() => setShowContactModal(true)}
                className="btn btn-primary"
              >
                Contact Someone
              </button>
            </PaymentGate>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-6">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 h-[600px]">
            {/* Conversation List */}
            <div className="border-r border-gray-200 p-4 overflow-y-auto">
              <h3 className="font-semibold mb-4">Conversations</h3>
              {renderConversationList()}
            </div>

            {/* Conversation Detail */}
            <div className="md:col-span-2">
              {renderConversationDetail()}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Contact Someone</h2>
              <button
                onClick={() => setShowContactModal(false)}
                className="text-2xl hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Select User</label>
                <select className="form-input">
                  <option>Choose a user to contact...</option>
                  <option>Sarah M. (Social Butterfly - 85% Match)</option>
                  <option>Mike R. (Adventure Seeker - 92% Match)</option>
                  <option>Emma L. (Cultural Explorer - 78% Match)</option>
                </select>
              </div>

              <div>
                <label className="form-label">Reason for Contact</label>
                <textarea
                  value={contactReason}
                  onChange={(e) => setContactReason(e.target.value)}
                  className="form-input"
                  rows="3"
                  placeholder="Why would you like to contact this person?"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Send a contact request with your reason</li>
                  <li>• The other person can accept or decline</li>
                  <li>• If accepted, you can start messaging</li>
                  <li>• Both users must be paid members to continue chatting</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => handleRequestContact('user123', contactReason)}
                  className="btn btn-primary flex-1"
                >
                  Send Request
                </button>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

